import { supabase } from '@/integrations/supabase/client';
import { driverProfileOf } from '@/lib/driverProfile';

// ─── Stage definitions ──────────────────────────────────────────────────────

export type VehicleStage = 'reserved' | 'deposit' | 'booked' | 'shipped' | 'balance_due' | 'balance' | 'tax_bill' | 'port_charges' | 'ready' | 'archived' | 'terminated' | 'sold' | 'draft' | 'active' | 'completed';

export const PROCUREMENT_STAGE_KEYS: VehicleStage[] = ['reserved', 'deposit', 'booked', 'shipped', 'balance_due', 'balance', 'tax_bill', 'port_charges', 'ready', 'archived'];
export const BOOKED_AND_BEYOND_STAGES: VehicleStage[] = ['booked', 'shipped', 'balance_due', 'balance', 'tax_bill', 'port_charges', 'ready'];
export const CONTRACT_STAGE_KEYS: VehicleStage[] = ['draft', 'active', 'completed', 'terminated', 'sold'];

export const PROCUREMENT_STAGE_CONFIG: { key: VehicleStage; label: string; color: string }[] = [
    { key: 'reserved',     label: 'Reserved',     color: 'slate' },
    { key: 'deposit',      label: 'Deposit',      color: 'amber' },
    { key: 'booked',       label: 'Booked',       color: 'violet' },
    { key: 'shipped',      label: 'Shipped',      color: 'cyan' },
    { key: 'balance_due',  label: 'Balance Due',  color: 'red' },
    { key: 'balance',      label: 'Balance',      color: 'orange' },
    { key: 'tax_bill',     label: 'Tax Bill',     color: 'indigo' },
    { key: 'port_charges', label: 'Port Charges', color: 'blue' },
    { key: 'ready',        label: 'Ready',        color: 'green' },
    { key: 'archived',     label: 'Archived',     color: 'gray' },
];

export const CONTRACT_STAGE_CONFIG: { key: VehicleStage; label: string; color: string }[] = [
    { key: 'draft',        label: 'Draft',        color: 'amber' },
    { key: 'active',       label: 'Active',       color: 'emerald' },
    { key: 'completed',    label: 'Completed',    color: 'slate' },
    { key: 'terminated',   label: 'Terminated',   color: 'red' },
    { key: 'sold',         label: 'Sold',         color: 'slate' },
];

export const STAGE_BADGE_STYLES: Record<VehicleStage, string> = {
    reserved:     'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
    deposit:      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
    booked:       'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-400',
    shipped:      'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-400',
    balance_due:  'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400',
    balance:      'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-400',
    tax_bill:     'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-400',
    port_charges: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400',
    ready:        'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400',
    archived:     'border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-400',
    terminated:   'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400',
    sold:         'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
    draft:        'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
    active:       'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    completed:    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
};

export const STAGE_LABELS: Record<VehicleStage, string> = {
    reserved: 'Reserved', deposit: 'Deposit', booked: 'Booked', shipped: 'Shipped', balance_due: 'Balance Due', balance: 'Balance',
    tax_bill: 'Tax Bill', port_charges: 'Port Charges', ready: 'Ready', archived: 'Archived',
    terminated: 'Terminated', sold: 'Sold', draft: 'Draft', active: 'Active', completed: 'Completed',
};

// ─── Stage computation ──────────────────────────────────────────────────────

export function computeVehicleStage(
    transactionTypes: string[],
    contractStatus: string | undefined,
    estArrivalAt: string | null | undefined,
    shipDate: string | null | undefined,
    hasShippingDetails: boolean,
    archivedAt: string | null | undefined,
): VehicleStage | null {
    // Contract status takes priority for active lifecycle stages — a vehicle that reached a contract
    // is never 'archived', even if archived_at is (stale-)set.
    if (contractStatus === 'COMPLETED') return 'completed';
    if (contractStatus === 'IN_PROGRESS') return 'active';
    if (contractStatus === 'DRAFT') return 'draft';
    if (contractStatus === 'TERMINATED') return 'terminated';

    // Archived vehicles never made it to a contract (third-party sale, third-party procurement, or
    // internal use) — checked before any transaction-derived stage so archiving e.g. a 'ready' vehicle
    // moves it to Archived.
    if (archivedAt) return 'archived';

    // No contract → use transaction-based stages

    const types = new Set(transactionTypes);

    const isReady = types.has('landed_cost') || types.has('steering_changed');
    if (isReady) return 'ready';

    if (types.has('port_charges')) return 'port_charges';
    if (types.has('tax_bill')) return 'tax_bill';

    const hasFullCif = types.has('cif_30') && types.has('cif_70');
    if (hasFullCif) return 'balance';

    const hasDeposit = types.has('cif_30');
    if (hasDeposit) {
        // If arrival is within 24 days, flag as balance_due
        if (estArrivalAt) {
            const arrival = new Date(estArrivalAt);
            const now = new Date();
            const diffDays = Math.ceil((arrival.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays <= 24) return 'balance_due';
        }
        // Vehicle has been shipped (today >= ship_date)
        if (shipDate && new Date() >= new Date(shipDate)) return 'shipped';
        // Vehicle has shipping details (ship_name, voyage_no, etc.) but hasn't shipped yet
        if (hasShippingDetails) return 'booked';
        return 'deposit';
    }

    // No deposit transactions → reserved (vehicle exists but no payments yet)
    return 'reserved';
}

// ─── Contracts map fetch ────────────────────────────────────────────────────

export interface ContractDriver { first_name: string; last_name: string; photo: string | null }

export async function fetchVehicleContractsMap(): Promise<{ statusMap: Map<number, string>; terminationDateMap: Map<number, string>; driverMap: Map<number, ContractDriver[]> }> {
    const { data, error } = await supabase
        .from('contracts')
        .select('vehicle_id, contract_updates(status, created_at, occurred_at), driver:drivers!inner(profile:profiles!inner(id, admin_id, first_name, last_name, photo))')
        .order('created_at', { ascending: false });
    if (error) throw error;
    const statusMap = new Map<number, string>();
    // When the winning update is TERMINATED, its occurred_at is the termination date (created_at is
    // only when the row was written) — see ContractRepaymentsOutstanding.
    const terminationDateMap = new Map<number, string>();
    const driverMap = new Map<number, { first_name: string; last_name: string; photo: string | null }[]>();
    data?.forEach((contract: any) => {
        if (!statusMap.has(contract.vehicle_id)) {
            const updates = contract.contract_updates || [];
            if (updates.length > 0) {
                const sorted = [...updates].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                const latest = sorted[0];
                statusMap.set(contract.vehicle_id, latest.status);
                if (latest.status === 'TERMINATED') {
                    terminationDateMap.set(contract.vehicle_id, latest.occurred_at ?? latest.created_at);
                }
            }
        }
        // THE DRIVER'S OWN PROFILE, NOT THE FIRST OF THE EMBED. `profiles.driver_id` is unique
        // only WHERE `admin_id IS NULL`, so an admin testing as this driver holds a second row
        // against the same id — see `driverProfileOf`. Name and photo come off the one row.
        const profile = driverProfileOf(contract.driver?.profile);
        if (profile) {
            const existing = driverMap.get(contract.vehicle_id) || [];
            const name = `${profile.first_name} ${profile.last_name}`;
            if (!existing.some(d => `${d.first_name} ${d.last_name}` === name)) {
                existing.push({
                    first_name: profile.first_name || '',
                    last_name: profile.last_name || '',
                    photo: profile.photo || null,
                });
                driverMap.set(contract.vehicle_id, existing);
            }
        }
    });
    return { statusMap, terminationDateMap, driverMap };
}
